import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, type Variants } from 'framer-motion';
import logoSrc from '@/assets/lg4.webp';

interface ConstructionLoaderProps {
  isVisible: boolean;
  durationMs?: number;
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

const nameWords = ['AST', 'Constructions'];

const wordVariants: Variants = {
  initial: { y: '100%' },
  animate: (i: number) => ({
    y: '0%',
    transition: { duration: 0.5, delay: 0.32 + i * 0.09, ease: [0.16, 1, 0.3, 1] },
  }),
};

export function ConstructionLoader({ isVisible, durationMs = 1400 }: ConstructionLoaderProps) {
  const [progress, setProgress] = useState(0);
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isVisible) return;

    setProgress(0);
    startTimeRef.current = null;

    const animate = (timestamp: number) => {
      if (startTimeRef.current === null) startTimeRef.current = timestamp;
      const elapsed = timestamp - startTimeRef.current;
      const t = Math.min(1, elapsed / durationMs);
      const eased = easeInOutCubic(t);
      setProgress(Math.round(eased * 100));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };

    const rafRef = { current: 0 };
    rafRef.current = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(rafRef.current);
  }, [isVisible, durationMs]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          className="fixed inset-0 z-200 flex items-center justify-center overflow-hidden"
          style={{ background: 'radial-gradient(ellipse at center, #0d1f3c 0%, #080f1e 100%)' }}
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Faint construction blueprint grid, drifting in slowly */}
          <motion.div
            className="absolute inset-0 opacity-[0.07]"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
              backgroundSize: '42px 42px',
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.07 }}
            transition={{ duration: 0.8 }}
          />

          {/* Ambient glow behind everything */}
          <motion.div
            className="absolute w-105 h-105 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(249,115,22,0.16) 0%, transparent 70%)' }}
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          />

          <div className="relative flex flex-col items-center gap-6 sm:gap-7 px-6 w-full max-w-xl">

            {/* Logo badge with orbiting ring + halo */}
            <motion.div
              className="relative w-36 h-36 sm:w-46 sm:h-46 shrink-0 flex items-center justify-center"
              initial={{ opacity: 0, scale: 0.5, rotate: -10 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
            >
              {/* Pulsing halo */}
              <motion.span
                className="absolute inset-0 rounded-full"
                style={{ background: 'radial-gradient(circle, rgba(249,115,22,0.45) 0%, transparent 72%)' }}
                animate={{ opacity: [0.5, 0.9, 0.5], scale: [0.92, 1.08, 0.92] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
              />

              {/* Orbiting frame — rounded-square to echo the logo card's own shape */}
              <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
                <rect x="3" y="3" width="94" height="94" rx="24" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1.5" />
                <motion.rect
                  x="3"
                  y="3"
                  width="94"
                  height="94"
                  rx="24"
                  fill="none"
                  stroke="#f97316"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  pathLength={1}
                  strokeDasharray="0.16 0.84"
                  animate={{ strokeDashoffset: [0, -1] }}
                  transition={{ duration: 2.2, repeat: Infinity, ease: 'linear' }}
                />
                <rect x="11" y="11" width="78" height="78" rx="19" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
                <motion.rect
                  x="11"
                  y="11"
                  width="78"
                  height="78"
                  rx="19"
                  fill="none"
                  stroke="#fdba74"
                  strokeWidth="1"
                  strokeLinecap="round"
                  pathLength={1}
                  strokeDasharray="0.09 0.91"
                  animate={{ strokeDashoffset: [0, 1] }}
                  transition={{ duration: 3.2, repeat: Infinity, ease: 'linear' }}
                />
              </svg>

              {/* Logo card */}
              <div className="relative w-27 h-27 sm:w-35 sm:h-35 rounded-2xl overflow-hidden bg-[#f8f9fb] shadow-[0_8px_28px_-6px_rgba(249,115,22,0.55)] ring-1 ring-white/10">
                <img src={logoSrc} alt="AST Constructions" className="w-full h-full object-cover" />
              </div>
            </motion.div>

            {/* Company name — words slide up out of a mask, one after another */}
            <div className="text-center px-4">
              <h1
                className="flex items-baseline justify-center gap-x-2.5 flex-wrap text-2xl sm:text-3xl md:text-5xl font-bold leading-tight"
                style={{ fontFamily: '"Poppins", sans-serif' }}
              >
                {nameWords.map((word, i) => (
                  <span key={word} className="inline-block overflow-hidden py-1">
                    <motion.span
                      className="inline-block"
                      style={{ color: i === 0 ? '#f97316' : '#ffffff' }}
                      custom={i}
                      variants={wordVariants}
                      initial="initial"
                      animate="animate"
                    >
                      {word}
                    </motion.span>
                  </span>
                ))}
              </h1>

              {/* Drawing underline */}
              <motion.div
                className="h-0.5 mx-auto mt-2 rounded-full"
                style={{ background: 'linear-gradient(90deg, transparent, #f97316, transparent)' }}
                initial={{ width: '0%', opacity: 0 }}
                animate={{ width: '60%', opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.6, ease: 'easeOut' }}
              />

              <motion.p
                className="mt-3 text-xs sm:text-sm font-medium tracking-wider uppercase"
                style={{ color: 'rgba(249,115,22,0.85)', fontFamily: '"Poppins", sans-serif' }}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.7, ease: 'easeOut' }}
              >
                Build Your Future
              </motion.p>
            </div>

            {/* Progress bar */}
            <motion.div
              className="w-full max-w-70"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.35, delay: 0.5 }}
            >
              <div
                className="relative h-1.5 w-full rounded-full overflow-hidden"
                style={{ background: 'rgba(255,255,255,0.08)', boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.5)' }}
              >
                <motion.div
                  className="absolute left-0 top-0 h-full rounded-full"
                  style={{ background: 'linear-gradient(90deg, #c2410c 0%, #f97316 70%, #fdba74 100%)' }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.12, ease: 'easeOut' }}
                >
                  <motion.div
                    className="absolute inset-0"
                    style={{
                      background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.45) 50%, transparent 100%)',
                      width: '50%',
                    }}
                    animate={{ x: ['-100%', '300%'] }}
                    transition={{ duration: 0.65, repeat: Infinity, ease: 'easeInOut', repeatDelay: 0.15 }}
                  />
                  <motion.div
                    className="absolute right-0 top-1/2 -translate-y-1/2 rounded-full"
                    style={{
                      width: 8,
                      height: 8,
                      background: '#fff7ed',
                      boxShadow: '0 0 8px 4px rgba(251,146,60,0.9), 0 0 18px 6px rgba(249,115,22,0.6)',
                    }}
                    animate={{ opacity: [0.8, 1, 0.8], scale: [0.9, 1.15, 0.9] }}
                    transition={{ duration: 0.5, repeat: Infinity, ease: 'easeInOut' }}
                  />
                </motion.div>
              </div>
            </motion.div>

          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
